import {
  ChatChannels,
  ChatChannel,
  getChatChannels,
  getUnreadChatChannelsCount,
} from "../../../../../fay/Api";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState, useCallback, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import {
  useNewChatChannelsRefetchInterval,
  useUnreadChatChannelCountRefetchInterval,
} from "./RefetchIntervals";
import { useEnableParallelChatAPICalls } from "./useChatMigration";
import { useChat } from "./State";
import { currentUserType } from "../../../utils";

// Define query key constants for better consistency and type safety
export const CHAT_QUERY_KEYS = {
  UNREAD_CHAT_CHANNELS_COUNT: ["unreadChatChannelsCount"],
  CHAT_CHANNELS: (searchTerm: string) => [
    "chatChannels",
    /**
     * NOTE: While we do local search, we don't need to invalidate the cache for it.
     * Uncomment once we conduct FAY-3180
     */
    // searchTerm
  ],
};

type UseChatChannelsUnreadCountReturn = {
  unreadChatChannelCount: number;
};

export const useChatChannelsUnreadCount =
  (): UseChatChannelsUnreadCountReturn => {
    const { enableChatAPICalls } = useEnableParallelChatAPICalls();
    const { data } = useQuery({
      enabled: enableChatAPICalls,
      queryKey: CHAT_QUERY_KEYS.UNREAD_CHAT_CHANNELS_COUNT,
      queryFn: async () => {
        return await getUnreadChatChannelsCount();
      },
      refetchInterval: useUnreadChatChannelCountRefetchInterval(),
    });

    return {
      unreadChatChannelCount: data ?? 0,
    };
  };

type UseChatChannelsReturn = {
  chatChannels: ChatChannel[];
  isLoading: boolean;
  loadMoreChatChannels: () => void;
  searchChatChannelsByPatientName: (searchTerm: string) => void;
  searchTerm: string;
  refreshChatChannels: () => void;
};

const chatChannelsKey = (searchTerm: string) =>
  CHAT_QUERY_KEYS.CHAT_CHANNELS(searchTerm);

export const useChatChannels = (): UseChatChannelsReturn => {
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();
  const { enableChatAPICalls } = useEnableParallelChatAPICalls();

  const { state, sortedChannels, dispatch } = useChat();

  const { fetchNextPage, isLoading, isFetchingNextPage, hasNextPage } =
    useInfiniteQuery({
      enabled: enableChatAPICalls,
      queryKey: chatChannelsKey(searchTerm),
      initialPageParam:
        sortedChannels.length > 0
          ? sortedChannels[sortedChannels.length - 1].latest_message?.sent_at
          : undefined,
      getNextPageParam: (lastPage: ChatChannels) =>
        lastPage.channels.length > 0 ? lastPage.earliest_updated_at : undefined,
      queryFn: async ({ pageParam }) => {
        const data = await getChatChannels({
          clientSearchQuery: searchTerm,
          limit: 50,
          updatedBefore: pageParam,
        });

        dispatch({
          category: "channel",
          type: "ADD_CHANNELS",
          payload: data.channels,
        });

        return data;
      },
      placeholderData: keepPreviousData,
    });

  useEffect(() => {
    // Keep fetching until there are none left.
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const refreshChatChannels = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: chatChannelsKey(searchTerm) });
  }, [queryClient, searchTerm]);

  useQuery({
    queryKey: ["newChatChannels", searchTerm],
    refetchInterval: useNewChatChannelsRefetchInterval(),
    enabled: enableChatAPICalls && !!state.latestChannelUpdatedAt,
    queryFn: async () => {
      const response = await getChatChannels({
        clientSearchQuery: searchTerm,
        limit: 10,
        updatedAfter: state.latestChannelUpdatedAt,
      });

      dispatch({
        category: "channel",
        type: "ADD_CHANNELS",
        payload: response.channels,
      });

      if (response.channels.length > 0) {
        // NOTE: Is this the most efficient way to update the cache?
        // We could update the chat channels with the new channels we fetch here.
        refreshChatChannels();
      }
      return response;
    },
  });

  // Apply search filter if needed
  const filteredChatChannels = useMemo(() => {
    if (!searchTerm) {
      return sortedChannels;
    }
    return sortedChannels.filter((channel) =>
      channel.respondent?.display_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, sortedChannels]);

  return {
    chatChannels: filteredChatChannels,
    isLoading,
    loadMoreChatChannels: fetchNextPage,
    searchChatChannelsByPatientName: setSearchTerm,
    searchTerm,
    refreshChatChannels: refreshChatChannels,
  };
};

export const useChatChannelByRespondentId = (
  respondentId: string | undefined,
) => {
  const { enableChatAPICalls } = useEnableParallelChatAPICalls();
  const { state, dispatch } = useChat();

  const chatChannelByRespondentId = useMemo(() => {
    return Object.values(state.channels)
      .filter((channel) => !!channel)
      .find((channel) => respondentId === channel!.chatChannel.respondent.id)
      ?.chatChannel;
  }, [state.channels, respondentId]);

  const queryResponse = useQuery({
    queryKey: ["respondentChatChannel", respondentId],
    enabled: enableChatAPICalls && !!respondentId,
    initialData: chatChannelByRespondentId,
    queryFn: async () => {
      if (!respondentId) return null;

      const filterPayload: {
        providerId?: string;
        patientId?: string;
      } = {};
      if (currentUserType === "Patient") {
        filterPayload.providerId = respondentId;
      } else if (currentUserType === "Provider") {
        filterPayload.patientId = respondentId;
      } else {
        throw new Error("Unknown user type");
      }

      const response = await getChatChannels({
        ...filterPayload,
        limit: 1,
      });

      if (response.channels.length === 0) {
        Sentry.captureMessage("No chat channel found for respondent ID", {
          extra: {
            respondentId,
          },
          level: "error",
        });
        return null;
      }

      dispatch({
        category: "channel",
        type: "ADD_CHANNELS",
        payload: response.channels,
      });

      return response.channels[0];
    },
  });

  return useMemo(
    () => ({
      ...queryResponse,
      // Override with data from the state; we add the channel to the state using the dispatch
      // above, and other parts of the system rely on the data from the state,
      // so we can't return the query data directly otherwise it would be out of sync from
      // the rest of the system.
      // During the next render this will be updated with the query data.
      data: chatChannelByRespondentId,
    }),
    [chatChannelByRespondentId, queryResponse],
  );
};
